<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Sparkles, Zap, ArrowLeft, ChevronRight, Check, Loader2, Users } from 'lucide-vue-next'
import {
  reportsApi,
  termsApi,
  type ClassReportResponse,
  type StudentWithReport,
  type StudentReportCard,
  type ReportContentEvent,
  type Term,
} from '@/api'
import { getStudentDisplayName } from '@/utils/student'
import { useGating } from '@/composables/useGating'
import { useAnalytics } from '@/composables/useAnalytics'
import { AppButton, EmptyState, GradePill, type PaletteHue } from '@/design-system/components'
import Monogram from '@/components/classes/Monogram.vue'

/**
 * Class Reports tab — term-scoped list of students, AI report generation (SSE
 * streamed), and an editable per-student report card. Ports the previously
 * orphaned pages/classes/Reports.vue onto the design system.
 */
const props = defineProps<{ classId: string; hue: PaletteHue }>()

type ViewState = 'list' | 'generate-setup' | 'generating' | 'report-card'

const { handleQuotaError } = useGating()
const { capture } = useAnalytics()

const viewState = ref<ViewState>('list')
const loading = ref(true)
const reportData = ref<ClassReportResponse | null>(null)
const error = ref<string | null>(null)

// Term filter
const terms = ref<Term[]>([])
const selectedTermId = ref<string>('')

// Generation
const selectedStudentIds = ref<Set<string>>(new Set())
const customiserText = ref('')
const isGenerating = ref(false)
const generationStudents = ref<StudentWithReport[]>([])
const generationProgress = ref<Map<string, 'pending' | 'generating' | 'complete'>>(new Map())
const generationTotal = ref(0)
const generationDone = ref(0)
let eventSource: EventSource | null = null

// Report card
const reportCard = ref<StudentReportCard | null>(null)
const reportCardLoading = ref(false)
const editingComment = ref('')
const isSavingComment = ref(false)

const progressPercent = computed(() =>
  generationTotal.value === 0 ? 0 : Math.round((generationDone.value / generationTotal.value) * 100),
)
const summary = computed(() => reportData.value?.summary)
const students = computed(() => reportData.value?.students ?? [])
const incompleteStudents = computed(() => students.value.filter((s) => !s.report?.id))
const allIncompleteSelected = computed(
  () =>
    incompleteStudents.value.length > 0 &&
    incompleteStudents.value.every((s) => selectedStudentIds.value.has(s.student.id)),
)
const reportProgressPercent = computed(() => {
  if (!summary.value || summary.value.totalStudents === 0) return 0
  return Math.round((summary.value.submittedReports / summary.value.totalStudents) * 100)
})

function toggleStudentSelection(studentId: string) {
  const next = new Set(selectedStudentIds.value)
  if (next.has(studentId)) next.delete(studentId)
  else next.add(studentId)
  selectedStudentIds.value = next
}
function toggleAllIncomplete() {
  selectedStudentIds.value = allIncompleteSelected.value
    ? new Set()
    : new Set(incompleteStudents.value.map((s) => s.student.id))
}

async function fetchTerms() {
  try {
    const res = await termsApi.termsGet()
    terms.value = res.data ?? []
  } catch {
    /* non-critical */
  }
}
async function fetchReports() {
  if (!props.classId) return
  loading.value = true
  error.value = null
  try {
    reportData.value = await reportsApi.getClassReports(
      props.classId,
      selectedTermId.value || undefined,
    )
  } catch (err) {
    console.error('Failed to load reports:', err)
    error.value = 'Failed to load reports'
  } finally {
    loading.value = false
  }
}

function openGenerateSetup() {
  selectedStudentIds.value = new Set(incompleteStudents.value.map((s) => s.student.id))
  customiserText.value = ''
  viewState.value = 'generate-setup'
}

async function startGeneration() {
  if (!props.classId || isGenerating.value) return
  const studentIds = [...selectedStudentIds.value]
  if (studentIds.length === 0) return

  generationStudents.value = students.value.filter((s) => studentIds.includes(s.student.id))
  isGenerating.value = true
  viewState.value = 'generating'
  generationTotal.value = studentIds.length
  generationDone.value = 0
  generationProgress.value = new Map()
  studentIds.forEach((id, index) =>
    generationProgress.value.set(id, index === 0 ? 'generating' : 'pending'),
  )

  try {
    const { jobId } = await reportsApi.generateReports({
      classId: props.classId,
      studentIds,
      termId: selectedTermId.value || undefined,
      customiser: customiserText.value.trim() || undefined,
    })

    eventSource = new EventSource(reportsApi.getReportStreamUrl(jobId), { withCredentials: true })
    eventSource.addEventListener('report', (e: MessageEvent) => {
      try {
        const data: ReportContentEvent = JSON.parse(e.data)
        if (data.studentId) {
          generationProgress.value.set(data.studentId, 'complete')
          generationDone.value++
          for (const id of studentIds) {
            if (generationProgress.value.get(id) === 'pending') {
              generationProgress.value.set(id, 'generating')
              break
            }
          }
        }
      } catch {
        /* ignore parse errors */
      }
    })
    eventSource.addEventListener('complete', finishGeneration)
    eventSource.onerror = finishGeneration
  } catch (err: unknown) {
    console.error('Failed to start report generation:', err)
    isGenerating.value = false
    handleQuotaError('ai', err)
    viewState.value = 'list'
  }
}
function finishGeneration() {
  closeEventSource()
  isGenerating.value = false
  fetchReports().then(() => {
    viewState.value = 'list'
  })
}
function closeEventSource() {
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
}

async function openReportCard(student: StudentWithReport) {
  viewState.value = 'report-card'
  reportCardLoading.value = true
  reportCard.value = null
  try {
    reportCard.value = await reportsApi.getStudentReportCard(props.classId, student.student.id)
    editingComment.value = reportCard.value.report?.comment ?? ''
    capture('view_student_report')
  } catch (err) {
    console.error('Failed to load report card:', err)
  } finally {
    reportCardLoading.value = false
  }
}
async function saveComment() {
  const reportId = reportCard.value?.reportId
  if (!reportId || isSavingComment.value) return
  isSavingComment.value = true
  try {
    await reportsApi.updateReportComment(reportId, editingComment.value)
    capture('add_student_report_comment')
    if (reportCard.value?.report) reportCard.value.report.comment = editingComment.value
  } catch (err) {
    console.error('Failed to save comment:', err)
  } finally {
    isSavingComment.value = false
  }
}
function backToList() {
  viewState.value = 'list'
  reportCard.value = null
}
function getGenStatus(studentId: string) {
  return generationProgress.value.get(studentId) ?? 'pending'
}
function pctText(val: number | undefined | null): string {
  return val == null ? '—' : `${Math.round(val)}%`
}

onMounted(() => {
  fetchTerms()
  fetchReports()
})
onUnmounted(closeEventSource)
watch(selectedTermId, fetchReports)
</script>

<template>
  <div class="rp">
    <!-- ===================== LIST ===================== -->
    <template v-if="viewState === 'list'">
      <div v-if="loading" class="rp__muted">Loading reports…</div>

      <div v-else-if="error" class="rp__card rp__error">
        <p>{{ error }}</p>
        <AppButton variant="secondary" size="sm" @click="fetchReports">Try again</AppButton>
      </div>

      <template v-else>
        <!-- Summary + AI banner -->
        <div class="rp__card">
          <div class="rp__head">
            <div class="rp__titlerow">
              <h2 class="rp__title">Reports</h2>
              <select v-if="terms.length" v-model="selectedTermId" class="rp__term">
                <option value="">All terms</option>
                <option v-for="term in terms" :key="term.id" :value="term.id">{{ term.name }}</option>
              </select>
            </div>
            <p v-if="summary" class="rp__sub">
              {{ summary.submittedReports }} of {{ summary.totalStudents }} students have reports
            </p>
          </div>

          <div v-if="summary && summary.totalStudents > 0" class="rp__bar">
            <div class="rp__bar-fill" :style="{ width: `${reportProgressPercent}%`, background: hue.solid }" />
          </div>

          <div class="rp__ai" :style="{ background: hue.soft }">
            <span class="rp__ai-icon" :style="{ background: hue.ink }"><Sparkles :size="18" /></span>
            <div class="rp__ai-body">
              <div class="rp__ai-title">AI report generation</div>
              <div class="rp__ai-sub">Generate personalised reports for students, drawing on their grades and attendance.</div>
            </div>
            <AppButton variant="primary" size="sm" :disabled="!students.length" @click="openGenerateSetup">
              <Zap :size="14" /> Generate reports
            </AppButton>
          </div>
        </div>

        <!-- Student list -->
        <EmptyState
          v-if="!students.length"
          :icon="Users"
          :hue="hue"
          title="No students yet"
          body="Add students to this class to generate reports."
        />
        <div v-else class="rp__card rp__list">
          <button
            v-for="entry in students"
            :key="entry.student.id"
            type="button"
            class="rp__row"
            @click="openReportCard(entry)"
          >
            <Monogram :first="entry.student.firstName" :last="entry.student.lastName" :key-id="entry.student.id" :photo-url="entry.student.photoUrl" :size="34" />
            <div class="rp__rowid">
              <div class="rp__rowname"><b>{{ entry.student.lastName }}</b>, {{ entry.student.firstName }}</div>
              <div v-if="entry.student.schoolId" class="rp__sid">{{ entry.student.schoolId }}</div>
            </div>
            <span class="rp__status" :class="entry.report?.id ? 'rp__status--done' : 'rp__status--todo'">
              {{ entry.report?.id ? 'Complete' : 'Incomplete' }}
            </span>
            <GradePill :pct="entry.finalGradePercentage ?? null" />
            <span class="rp__att">{{ pctText(entry.attendancePercentage) }}</span>
            <ChevronRight :size="15" class="rp__chev" />
          </button>
        </div>
      </template>
    </template>

    <!-- ===================== GENERATE SETUP ===================== -->
    <template v-else-if="viewState === 'generate-setup'">
      <button type="button" class="rp__back" @click="viewState = 'list'"><ArrowLeft :size="15" /> Back to reports</button>
      <div class="rp__card">
        <div class="rp__setup-head">
          <span class="rp__ai-icon" :style="{ background: hue.ink }"><Sparkles :size="18" /></span>
          <div>
            <h2 class="rp__title">Generate reports</h2>
            <p class="rp__sub">Select students and, optionally, steer the AI.</p>
          </div>
        </div>

        <label class="rp__label">AI guidance (optional)</label>
        <textarea
          v-model="customiserText"
          rows="3"
          class="rp__textarea"
          placeholder="e.g. focus on effort and behaviour, keep comments positive, mention specific skills…"
        />

        <div class="rp__selhead">
          <label class="rp__label">Students ({{ selectedStudentIds.size }} selected)</label>
          <button v-if="incompleteStudents.length" type="button" class="rp__link" :style="{ color: hue.ink }" @click="toggleAllIncomplete">
            {{ allIncompleteSelected ? 'Deselect all' : 'Select all incomplete' }}
          </button>
        </div>
        <div class="rp__sellist">
          <label
            v-for="entry in students"
            :key="entry.student.id"
            class="rp__selrow"
            :class="{ 'rp__selrow--has': entry.report?.id }"
          >
            <input
              type="checkbox"
              :checked="selectedStudentIds.has(entry.student.id)"
              :disabled="!!entry.report?.id"
              @change="toggleStudentSelection(entry.student.id)"
            />
            <Monogram :first="entry.student.firstName" :last="entry.student.lastName" :key-id="entry.student.id" :photo-url="entry.student.photoUrl" :size="28" />
            <span class="rp__selname">{{ getStudentDisplayName(entry.student) }}</span>
            <span v-if="entry.report?.id" class="rp__status rp__status--done">Has report</span>
          </label>
        </div>

        <div class="rp__actions">
          <AppButton variant="ghost" size="md" @click="viewState = 'list'">Cancel</AppButton>
          <AppButton variant="primary" size="md" :disabled="selectedStudentIds.size === 0" @click="startGeneration">
            <Zap :size="15" /> Generate {{ selectedStudentIds.size }} report{{ selectedStudentIds.size === 1 ? '' : 's' }}
          </AppButton>
        </div>
      </div>
    </template>

    <!-- ===================== GENERATING ===================== -->
    <template v-else-if="viewState === 'generating'">
      <div class="rp__card">
        <div class="rp__setup-head">
          <span class="rp__ai-icon rp__ai-icon--pulse" :style="{ background: hue.ink }"><Sparkles :size="18" /></span>
          <div>
            <h2 class="rp__title">Generating reports</h2>
            <p class="rp__sub">{{ generationDone }} of {{ generationTotal }} complete</p>
          </div>
        </div>
        <div class="rp__bar rp__bar--tall">
          <div class="rp__bar-fill" :style="{ width: `${progressPercent}%`, background: hue.solid }" />
        </div>
        <ul class="rp__genlist">
          <li
            v-for="entry in generationStudents"
            :key="entry.student.id"
            class="rp__genrow"
            :class="`rp__genrow--${getGenStatus(entry.student.id)}`"
          >
            <span class="rp__genicon">
              <span v-if="getGenStatus(entry.student.id) === 'pending'" class="rp__dot" />
              <Loader2 v-else-if="getGenStatus(entry.student.id) === 'generating'" :size="16" class="rp__spin" :style="{ color: hue.ink }" />
              <Check v-else :size="16" :style="{ color: 'var(--success-ink)' }" />
            </span>
            <span class="rp__genname">{{ getStudentDisplayName(entry.student) }}</span>
          </li>
        </ul>
      </div>
    </template>

    <!-- ===================== REPORT CARD ===================== -->
    <template v-else-if="viewState === 'report-card'">
      <button type="button" class="rp__back" @click="backToList"><ArrowLeft :size="15" /> Back to reports</button>

      <div v-if="reportCardLoading" class="rp__muted">Loading report…</div>

      <div v-else-if="reportCard" class="rp__card">
        <div class="rp__cardhead">
          <Monogram :first="reportCard.student.firstName" :last="reportCard.student.lastName" :key-id="reportCard.student.id" :photo-url="reportCard.student.photoUrl" :size="52" />
          <div class="rp__cardid">
            <h2 class="rp__title">{{ getStudentDisplayName(reportCard.student) }}</h2>
            <div class="rp__cardmeta">
              <GradePill :pct="reportCard.finalGrade?.percentage ?? null" size="lg" />
              <span v-if="reportCard.finalGrade?.letter" class="rp__letter">{{ reportCard.finalGrade.letter }}</span>
              <span v-if="reportCard.reportId" class="rp__status rp__status--done">Report complete</span>
              <span v-else class="rp__status rp__status--todo">No report yet</span>
            </div>
          </div>
        </div>

        <!-- Attendance -->
        <div v-if="reportCard.attendance" class="rp__section">
          <div class="rp__attrow">
            <span class="rp__seclabel">Attendance</span>
            <span class="rp__attpct">{{ pctText(reportCard.attendance.presentPercentage) }} present · {{ reportCard.attendance.totalLessons }} lessons</span>
          </div>
          <div class="rp__attbar">
            <div v-if="reportCard.attendance.presentCount" :style="{ width: `${(reportCard.attendance.presentCount / reportCard.attendance.totalLessons) * 100}%`, background: 'var(--success-solid)' }" />
            <div v-if="reportCard.attendance.lateCount" :style="{ width: `${(reportCard.attendance.lateCount / reportCard.attendance.totalLessons) * 100}%`, background: 'var(--warning-solid)' }" />
            <div v-if="reportCard.attendance.absentCount" :style="{ width: `${(reportCard.attendance.absentCount / reportCard.attendance.totalLessons) * 100}%`, background: 'var(--danger-solid)' }" />
          </div>
          <div class="rp__attkey">
            <span><span class="rp__kd" style="background: var(--success-solid)" /> {{ reportCard.attendance.presentCount }} present</span>
            <span><span class="rp__kd" style="background: var(--warning-solid)" /> {{ reportCard.attendance.lateCount }} late</span>
            <span><span class="rp__kd" style="background: var(--danger-solid)" /> {{ reportCard.attendance.absentCount }} absent</span>
          </div>
        </div>

        <!-- Category grades -->
        <div v-if="reportCard.categoryGrades?.length" class="rp__section">
          <div class="rp__seclabel">Grades by category</div>
          <div v-for="cat in reportCard.categoryGrades" :key="cat.category.id" class="rp__catrow">
            <span class="rp__catname">{{ cat.category.name }}</span>
            <div class="rp__catbar">
              <div class="rp__catbar-fill" :style="{ width: `${cat.percentage ?? 0}%`, background: cat.category.colour || hue.solid }" />
            </div>
            <span class="rp__catpct">{{ pctText(cat.percentage) }}</span>
          </div>
        </div>

        <!-- Teacher comment -->
        <div class="rp__section">
          <div class="rp__seclabel">Teacher comment</div>
          <textarea v-model="editingComment" rows="6" class="rp__textarea" placeholder="Write a comment for this student's report…" />
          <div class="rp__actions">
            <AppButton
              variant="primary"
              size="md"
              :loading="isSavingComment"
              :disabled="!reportCard.reportId || editingComment === (reportCard.report?.comment ?? '')"
              @click="saveComment"
            >
              Save comment
            </AppButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.rp {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--font-sans);
}
.rp__muted {
  padding: 32px 4px;
  font-size: 13.5px;
  color: var(--ink-400);
}
.rp__card {
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  background: var(--surface);
  padding: 20px;
}
.rp__error {
  text-align: center;
  color: var(--danger-ink);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.rp__head {
  margin-bottom: 14px;
}
.rp__titlerow {
  display: flex;
  align-items: center;
  gap: 12px;
}
.rp__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: var(--text-primary);
}
.rp__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--ink-500);
}
.rp__term {
  font-size: 12.5px;
  color: var(--ink-600);
  background: var(--canvas);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-sm);
  padding: 4px 8px;
}
.rp__bar {
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--ink-100);
  overflow: hidden;
  margin-bottom: 16px;
}
.rp__bar--tall {
  height: 10px;
  margin: 4px 0 18px;
}
.rp__bar-fill {
  height: 100%;
  border-radius: var(--radius-pill);
  transition: width 0.4s ease;
}
.rp__ai {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: var(--radius-md);
}
.rp__ai-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.rp__ai-icon--pulse {
  animation: rp-pulse 1.4s ease-in-out infinite;
}
@keyframes rp-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
.rp__ai-body {
  flex: 1;
  min-width: 0;
}
.rp__ai-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.rp__ai-sub {
  font-size: 12.5px;
  color: var(--ink-600);
  margin-top: 1px;
}
.rp__list {
  padding: 6px;
}
.rp__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.rp__row:hover {
  background: var(--ink-50);
}
.rp__rowid {
  flex: 1;
  min-width: 0;
}
.rp__rowname {
  font-size: 14px;
  color: var(--ink-600);
}
.rp__rowname b {
  font-weight: 600;
  color: var(--text-primary);
}
.rp__sid {
  font-size: 11.5px;
  color: var(--ink-400);
  font-family: var(--font-mono);
}
.rp__att {
  font-size: 12px;
  color: var(--ink-400);
  font-variant-numeric: tabular-nums;
  width: 34px;
  text-align: right;
}
.rp__chev {
  color: var(--ink-300);
}
.rp__status {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}
.rp__status--done {
  background: var(--success-soft);
  color: var(--success-ink);
}
.rp__status--todo {
  background: var(--ink-100);
  color: var(--ink-500);
}
.rp__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-500);
}
.rp__back:hover {
  color: var(--text-primary);
}
.rp__setup-head,
.rp__cardhead {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.rp__cardid {
  min-width: 0;
}
.rp__cardmeta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}
.rp__letter {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-600);
}
.rp__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-700, var(--text-primary));
  margin-bottom: 8px;
}
.rp__textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: 13.5px;
  color: var(--text-primary);
  background: var(--surface);
  resize: vertical;
}
.rp__textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
}
.rp__selhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 18px 0 8px;
}
.rp__link {
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}
.rp__sellist {
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  max-height: 320px;
  overflow-y: auto;
}
.rp__selrow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  cursor: pointer;
  border-top: 1px solid var(--hairline, var(--outline-variant));
}
.rp__selrow:first-child {
  border-top: 0;
}
.rp__selrow:hover {
  background: var(--ink-50);
}
.rp__selrow--has {
  opacity: 0.55;
}
.rp__selname {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  color: var(--text-primary);
}
.rp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
.rp__genlist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rp__genrow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: var(--ink-50);
}
.rp__genrow--generating {
  background: var(--canvas);
}
.rp__genrow--complete {
  background: var(--success-soft);
}
.rp__genicon {
  width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rp__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--ink-300);
}
.rp__spin {
  animation: rp-spin 0.8s linear infinite;
}
@keyframes rp-spin {
  to { transform: rotate(360deg); }
}
.rp__genname {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary);
}
.rp__section {
  margin-top: 20px;
}
.rp__seclabel {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-600);
  margin-bottom: 10px;
}
.rp__attrow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}
.rp__attrow .rp__seclabel {
  margin-bottom: 0;
}
.rp__attpct {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
.rp__attbar {
  display: flex;
  height: 14px;
  border-radius: var(--radius-pill);
  overflow: hidden;
  background: var(--ink-100);
}
.rp__attkey {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 12px;
  color: var(--ink-500);
}
.rp__attkey span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.rp__kd {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.rp__catrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.rp__catname {
  width: 120px;
  font-size: 13px;
  color: var(--ink-600);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rp__catbar {
  flex: 1;
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--ink-100);
  overflow: hidden;
}
.rp__catbar-fill {
  height: 100%;
  border-radius: var(--radius-pill);
}
.rp__catpct {
  width: 44px;
  text-align: right;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-600);
  font-variant-numeric: tabular-nums;
}
</style>
